/* eslint-disable react/destructuring-assignment */
import { CloseOutlined, InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { useLazyQuery, useQuery } from "@apollo/client";
import { Button, Dropdown, Form, Input, message, Popover, Tooltip } from "antd";
import { capitalize, isEmpty, isNull, isUndefined } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GET_AI_RESPONSE, GET_AI_SUGGESTION_QUICK_TIP } from "../../../graphql/query";
import useLang from "../../../hooks/useLang";
import { EngagedAiIcon2 } from "../../../icons/EngagedAiIcon";

import { UserContext } from "../../../amplify/authenticator-provider";
import { DownIcon, EmailAiIcon, EmailIcon, GreyPersonIcon } from "../../../icons/js/EmailIcons";
import PlusOutLinedJSX from "../../../icons/js/PlusOutLinedJSX";
import { applicationUseCaseMapping, getApplicationUseCaseId } from "../../../library/utils";
import { setselectedEmail, setselectedText } from "../../../redux/slices/leadSlice";
import { setEmailAddress, setEmailBody, setEmailSubject } from "../../../redux/slices/userSlice";
import { setEmailAiResponseHistoryId } from "../../../redux/slices/aiSlice";
import RichTextEditorWithAudioRecorder from "./RichTextEditorWithAudioRecorder";
import { AntdFormLabel } from "../../commonComponents/AntdFormsComponents";
import { addFilesToAttachments } from "./engagementActivity";
import { ADDING_TYPE } from "../../adminDashboard/settings/CustomizeAiSuggestions";

/**
 *
 * @param {{textDisabled:()=>boolean,emailTextRef:any,setEmailCursor:()=>any,email:string,setemail:import("react").Dispatch<import("react").SetStateAction<string>>,emailMode:"edit"|"select",emailForm:import("antd").FormInstance,handleEmail:()=>void,className:string,showTo:boolean,showSubject:boolean,to?:string,disableToEmail?:boolean,body:string}} props
 * @returns
 */
export const EmailTextBox = props => {
  const {
    emailMode = "select",
    showTo = true,
    showSubject = true,
    disableToEmail = false,
    useFlexLayout = false,
  } = props;
  const dispatch = useDispatch();
  const { selectedLead } = useSelector(state => state.lead);
  const { selectedEmail } = useSelector(state => state.lead);
  const { selectedText } = useSelector(state => state.lead);

  const replaceSelectedText = newText => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(newText));
    }
  };
  useEffect(() => {
    if (isEmpty(props.to)) {
      const tmpSelectedEmail =
        (selectedLead?.emails || [])
          .filter(email => !isEmpty(email.email) && email.isPrimary)
          .sort((a, b) => {
            // Handle both string and numeric IDs
            const idA = a?.id;
            const idB = b?.id;

            if (isUndefined(idA) || isNull(idA)) return 1; // null/undefined values at the end
            if (isUndefined(idB) || isNull(idB)) return -1;

            // Otherwise use numeric comparison
            return idA - idB;
          })[0] || null; // Fallback to null if no match
      if (tmpSelectedEmail) {
        dispatch(setselectedEmail(tmpSelectedEmail));
        dispatch(setEmailAddress(tmpSelectedEmail.email));
      }
    } else {
      dispatch(setselectedEmail({ email: props.to }));
    }
  }, [selectedLead]);
  useEffect(() => {
    if (selectedEmail?.email) {
      props.emailForm.setFieldValue("to", selectedEmail?.email);
      props.emailTextRef.current.focus();
    }
  }, [selectedEmail?.email]);
  useEffect(() => {
    if (props.body) {
      props.emailTextRef.current.innerHTML = props.body;
    }
  }, [props.body]);
  useEffect(() => {
    props.emailForm.setFieldValue("to", props.email);
  }, [props.email]);
  const [previousLeadId, setPreviousLeadId] = useState(null);

  useEffect(() => {
    if (!isEmpty(selectedEmail)) {
      dispatch(setEmailAddress(selectedEmail.email));
    }
  }, [selectedEmail]);

  // Only reset form when lead actually changes, not on every re-render
  useEffect(() => {
    const currentLeadId = selectedLead?.id;
    if (currentLeadId !== previousLeadId && previousLeadId !== null) {
      props.emailForm.resetFields();
      setPreviousLeadId(currentLeadId);
    } else if (previousLeadId === null) {
      setPreviousLeadId(currentLeadId);
    }
  }, [selectedLead?.id, previousLeadId, props.emailForm]);
  return (
    <div className={useFlexLayout ? "pt-1 h-full flex flex-col" : "pt-1 h-full"}>
      <Form
        className={useFlexLayout ? "flex-1 flex flex-col" : ""}
        onFinish={props.handleEmail}
        form={props.emailForm}
      >
        {showTo && (
          <div className="flex-row-centered gap-1 px-1">
            <span className="text-grey-700 text-[14px] font-semibold">To:</span>
            {emailMode === "select" ? (
              <Dropdown
                menu={{
                  items: (selectedLead?.emails || [])
                    .filter(item => !isEmpty(item.email))
                    .map(mail => ({
                      label: (
                        <div
                          onClick={() => {
                            dispatch(setselectedEmail(mail));
                            dispatch(setEmailAddress(mail.email));
                          }}
                          className="flex-row-centered justify-between items-center w-full"
                        >
                          <span className="flex-row-centered gap-4">
                            <div className="flex-row-centered gap-1">
                              <GreyPersonIcon />
                              <span>{selectedLead.fullName}</span>
                            </div>
                            <span>
                              {mail.email.substring(0, 35)} ({capitalize(mail.emailType)})
                            </span>
                          </span>
                          {mail.isPrimary && (
                            <span className="xs-600 text-grey-700">Primary Email</span>
                          )}
                        </div>
                      ),
                      key: mail.id,
                    })),
                }}
                overlayClassName="w-1/3 scrollable-dropdown"
                trigger="click"
              >
                {!isEmpty(selectedEmail) ? (
                  <Tooltip title={selectedLead.fullName}>
                    <span className="border border-solid flex-row-centered cursor-pointer gap-1 rounded-[30px] p-1 px-2 sm-400 bg-grey-50 text-grey-700 border-grey-200">
                      <GreyPersonIcon /> {selectedEmail?.email} <DownIcon size="20px" />
                    </span>
                  </Tooltip>
                ) : (
                  <span className="cursor-pointer">Select Email</span>
                )}
              </Dropdown>
            ) : (
              <Form.Item
                className="email-label"
                name="to"
                rules={[
                  { required: true, message: "Please enter an email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input
                  // value={props.email}
                  name="to"
                  disabled={disableToEmail}
                  className="w-[300px]"
                  onChange={e => {
                    // props.setemail(e.target.value);
                    dispatch(setEmailAddress(e.target.value));
                  }}
                  placeholder="Enter the email address"
                  variant="borderless"
                />
              </Form.Item>
            )}
          </div>
        )}
        <Form.Item className="hidden" name="to" />
        {showSubject && (
          <>
            {showTo && <div className="w-full mt-1 h-[1px] bg-grey-200" />}
            <Form.Item
              name="subject"
              rules={[{ required: true, message: "Please enter subject", whitespace: true }]}
              className="px-2 !mb-0"
              label={<AntdFormLabel label="Subject" />}
            >
              <Input
                disabled={props.textDisabled()}
                variant="borderless"
                name="subject"
                onChange={e => dispatch(setEmailSubject(e.target.value))}
                placeholder="Enter subject line here"
              />
            </Form.Item>
            <div className="w-full mt-1 h-[1px] bg-grey-200" />
          </>
        )}

        <div className={useFlexLayout ? "flex-1 min-h-0" : ""}>
          <RichTextEditorWithAudioRecorder
            useFlexLayout={useFlexLayout}
            editorHeight={useFlexLayout ? "" : props.className || "h-[20vh]"}
            editorCustomClassName="!border-none !p-2"
            toolBarPosition="bottom"
            propRef={props.emailTextRef}
            controllerClassName="bg-grey-50"
            setcontent={value => dispatch(setEmailBody(value))}
            textDisabled={props.textDisabled()}
            onSelect={value => {
              dispatch(setselectedText(value));
            }}
            selectedText={selectedText}
            getSelectionRange={value => {
              props.setEmailCursor(value.savedRange);
            }}
            onAttachFiles={
              props.setAttachments && !props.textDisabled()
                ? files => addFilesToAttachments(files, props.setAttachments)
                : null
            }
            FOBJSX={
              <FOBComponent
                emailTextRef={props.emailTextRef}
                replaceSelectedText={replaceSelectedText}
                selectedText={selectedText}
                emailForm={props.emailForm}
                emailMode={emailMode}
                showSubject={showSubject}
                showTo={showTo}
              />
            }
            allowRecording={false}
            showAttachImages={false}
            showAiPopup
          />
        </div>
      </Form>
    </div>
  );
};
/**
 *
 * @param {{emailTextRef:any,replaceSelectedText:any,emailForm:any}} props
 * @returns
 */
const FOBComponent = props => {
  const buttonRef = useRef(null);
  const { user } = useContext(UserContext);
  const { selectedLead } = useSelector(state => state.lead);
  const { applicationUseCases } = useSelector(state => state.aiData);
  const dispatch = useDispatch();
  const [getAiResponse, { loading: getAiResponseLoader }] = useLazyQuery(GET_AI_RESPONSE);
  const generateAiResponse = async () => {
    try {
      const res = await getAiResponse({
        fetchPolicy: "network-only",
        variables: {
          aiSuggestionUserInput: {
            quickTips: [],
            tones: [],
            additionalNote: "",
          },
          companyId: user.company.id,
          applicationUseCaseId: getApplicationUseCaseId(
            applicationUseCases,
            applicationUseCaseMapping.ENGAGEMENT_EMAIL_SUGGESTION
          )?.id,
          leadId: selectedLead.id,
        },
      });

      dispatch(setEmailAiResponseHistoryId(res?.data?.getAiResponse?.aiResponseHistoryId));

      if (res.data.getAiResponse.statusCode === 200) {
        const bodyContent = !isEmpty(res.data.getAiResponse.bodyHtml)
          ? res.data.getAiResponse.bodyHtml
          : !isEmpty(res.data.getAiResponse.body)
          ? res.data.getAiResponse.body
          : res.data.getAiResponse.content;
        if (props?.emailMode === "select") {
          props.emailTextRef.current.innerHTML = bodyContent;
          props.emailForm.setFieldValue("subject", res.data.getAiResponse.subject);
        } else {
          const container = document.getElementById("email-text-box-container");
          if (!container) {
            return;
          }

          const editor = container.querySelector("#rich-text");
          if (!editor) {
            return;
          }

          const replyInfo = editor.querySelector("#reply-info");
          if (!replyInfo) {
            return;
          }

          // Step 1: Find top-level block in editor that wraps replyInfo
          let preserveBlock = replyInfo;
          while (preserveBlock.parentNode && preserveBlock.parentNode !== editor) {
            preserveBlock = preserveBlock.parentNode;
          }

          // Step 2: Completely clear editor
          editor.innerHTML = "";

          // Step 3: Add preserveBlock back in
          editor.appendChild(preserveBlock);

          // Step 4: Clean up inside preserveBlock — remove anything BEFORE #reply-info
          const innerNodes = Array.from(preserveBlock.childNodes);
          let shouldRemove = true;

          innerNodes.forEach(node => {
            if (node === replyInfo) {
              shouldRemove = false;
            }
            if (shouldRemove) {
              preserveBlock.removeChild(node);
            }
          });

          // Step 5: Insert AI-generated content above preserveBlock
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = bodyContent;

          const fragment = document.createDocumentFragment();
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }

          editor.insertBefore(fragment, preserveBlock);
          editor.focus();
        }
      } else {
        message.error(res.data.getAiResponse.statusMessage);
      }
    } catch (err) {
      console.log(err);
    }
  };
  return (
    <div className="border-solid border flex-row-centered cursor-pointer items-center gap-1 border-grey-200 bg-white rounded">
      <Tooltip title="Use Otto Ai to write better emails" placement="left">
        <div
          className="p-1 flex-row-centered"
          onClick={() => {
            if (getAiResponseLoader) return;
            generateAiResponse();
          }}
        >
          {getAiResponseLoader ? (
            <LoadingOutlined className="animate-spin mr-2" />
          ) : (
            <EmailAiIcon size={22} />
          )}
        </div>
      </Tooltip>
      <div className="h-[30px] w-[1px] bg-grey-200" />
      <Tooltip title="Customize AI Suggestion">
        <Popover
          trigger="click"
          content={<CustomizeEmailAi {...props} buttonRef={buttonRef} />}
          placement="topRight"
          destroyTooltipOnHide
          overlayClassName="testing"
        >
          <div
            className="p-1 flex-row-centered cursor-pointer"
            onClick={e => e.stopPropagation()}
            ref={buttonRef}
          >
            <DownIcon size={22} />
          </div>
        </Popover>
      </Tooltip>
    </div>
  );
};
/**
 *
 * @param {{buttonRef:React.MutableRefObject,emailForm:import("antd").FormInstance<any>,emailTextRef:any}} props
 * @returns
 */
export const CustomizeEmailAi = props => {
  const [lang] = useLang();
  const { selectedLead } = useSelector(state => state.lead);
  const { user } = useContext(UserContext);
  const { applicationUseCases } = useSelector(state => state.aiData);
  const [quickActions, setquickActions] = useState({
    quickTips: "",
    writingTone: "",
    length: "",
    additionalNotes: "",
  });

  // Dynamic Quick Tips / Tones (admin-managed) — replaces the previous
  // hardcoded lists. Mirrors the SMS popover (see InputArea.js → CustomizePrompt)
  // but scoped to the email use case.
  const [dynamicTips, setdynamicTips] = useState([]);
  const [dynamicTones, setdynamicTones] = useState([]);
  const { loading: quickTipLoader } = useQuery(GET_AI_SUGGESTION_QUICK_TIP, {
    variables: {
      companyId: user.company.id,
      locationIds:
        Array.isArray(user?.departmentIds) && user.departmentIds.length > 0
          ? null
          : sessionStorage.getItem("currentLocationId")?.split(","),
      applicationUseCaseId: getApplicationUseCaseId(
        applicationUseCases,
        applicationUseCaseMapping.ENGAGEMENT_EMAIL_SUGGESTION
      )?.id,
      departmentIds: user?.departmentIds || null,
    },
    onCompleted: res => {
      const all = res?.getAiSuggestionQuickTip || [];
      setdynamicTips(all.filter(t => t?.isActive && t?.suggestionType === ADDING_TYPE.TIPS));
      setdynamicTones(all.filter(t => t?.isActive && t?.suggestionType === ADDING_TYPE.TONES));
    },
  });

  // Build dropdown menu items from a dynamic list, with loading/empty fallbacks.
  const buildMenuItems = (items, key) => {
    if (quickTipLoader) {
      return [{ key: "loading", label: "Loading...", disabled: true }];
    }
    if (isEmpty(items)) {
      return [{ key: "empty", label: "No options found", disabled: true }];
    }
    return items.map((item, index) => ({
      key: `${key}-${index}`,
      label: item.tip,
      onClick: () => setquickActions(prev => ({ ...prev, [key]: item.tip })),
    }));
  };

  const quickButtons = [
    {
      label: !isEmpty(quickActions.quickTips) ? quickActions.quickTips : lang.quickTips,
      menuItems: buildMenuItems(dynamicTips, "quickTips"),
    },
    {
      label: !isEmpty(quickActions.writingTone) ? quickActions.writingTone : lang.writingTone,
      menuItems: buildMenuItems(dynamicTones, "writingTone"),
    },
    {
      label: !isEmpty(quickActions.length) ? quickActions.length : lang.length,
      menuItems: [
        {
          label: lang.short,
          onClick: () => setquickActions({ ...quickActions, length: lang.short }),
        },
        {
          label: lang.medium,
          onClick: () => setquickActions({ ...quickActions, length: lang.medium }),
        },
        {
          label: lang.long,
          onClick: () => setquickActions({ ...quickActions, length: lang.long }),
        },
      ],
    },
  ];
  const close = () => {
    props.buttonRef && props.buttonRef.current.click();
  };
  const dispatch = useDispatch();
  const [getAiResponse, { loading: getAiResponseLoader }] = useLazyQuery(GET_AI_RESPONSE);
  const handleAiSuggestion = async () => {
    try {
      const res = await getAiResponse({
        fetchPolicy: "network-only",
        variables: {
          aiSuggestionUserInput: {
            quickTips: [quickActions.quickTips],
            tones: [quickActions.writingTone],
            additionalNote: quickActions.additionalNotes,
          },
          companyId: user.company.id,
          applicationUseCaseId: getApplicationUseCaseId(
            applicationUseCases,
            applicationUseCaseMapping.ENGAGEMENT_EMAIL_SUGGESTION
          )?.id,
          leadId: selectedLead.id,
        },
      });

      dispatch(setEmailAiResponseHistoryId(res?.data?.getAiResponse?.aiResponseHistoryId));

      if (res.data.getAiResponse.statusCode === 200) {
        const bodyContent = !isEmpty(res.data.getAiResponse.bodyHtml)
          ? res.data.getAiResponse.bodyHtml
          : !isEmpty(res.data.getAiResponse.body)
          ? res.data.getAiResponse.body
          : res.data.getAiResponse.content;
        if (props?.emailMode === "select") {
          props.emailTextRef.current.innerHTML = bodyContent;
          props.emailForm.setFieldValue("subject", res.data.getAiResponse.subject);
        } else {
          const container = document.getElementById("email-text-box-container");
          if (!container) {
            console.error("Parent container element not found");
            return;
          }
          const editor = container.querySelector("#rich-text");
          if (!editor) {
            console.error("Editor element not found within the container");
            return;
          }
          if (editor instanceof HTMLElement) {
            editor.focus(); // Ensure editor is focused
          } else {
            console.error("Editor element is not focusable");
          }
          const selection = window.getSelection();
          const range = selection.getRangeAt(0);
          range.deleteContents();
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = bodyContent;
          const fragment = document.createDocumentFragment();
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }
          range.insertNode(fragment);
        }

        close();
      } else {
        message.error(res.data.getAiResponse.statusMessage);
      }
    } catch (err) {
      console.log(err);
    }
  };
  return (
    <div className="flex-col-centered gap-2 w-[450px]">
      <div className="flex-row-centered w-full justify-between">
        <div className="flex-row-centered gap-2">
          <EngagedAiIcon2 />
          <span className="sm-600">Customize AI Suggestion</span>
        </div>
        <CloseOutlined onClick={close} />
      </div>
      <div className="flex flex-wrap gap-2 py-3">
        {quickButtons.map((item, index) => (
          <Dropdown key={index} menu={{ items: item.menuItems }}>
            <span className="cursor-pointer px-2 py-1 gap-2 flex-row-centered border border-solid rounded-[27px] border-grey-200">
              {item.label}
              <PlusOutLinedJSX color="black" />
            </span>
          </Dropdown>
        ))}
      </div>
      <div className="flex-col-centered gap-2 px-4 pt-2 pb-5">
        <div className="flex-row-centered gap-2">
          <span className="sm-600 text-grey-700">Additional Notes</span>
          <InfoCircleOutlined />
        </div>
        <Input.TextArea
          value={quickActions.additionalNotes}
          onChange={e => setquickActions({ ...quickActions, additionalNotes: e.target.value })}
          placeholder="Please enter the additional notes here"
        />
      </div>
      <Button
        onClick={handleAiSuggestion}
        loading={getAiResponseLoader}
        type="primary"
        className="max-w-max"
      >
        Generate Email
      </Button>
    </div>
  );
};
export const GenerateEmailJSX = props => {
  const text =
    "Hi [Customer's Name], \n I'm [Your Name], a Sales Professional at [Dealership Name]. I'd love to help you find the perfect car and make the process simple and hassle-free. \n\n Let me know if there's a specific vehicle you're interested in or if you'd like recommendations. Feel free to reply or call me at [Your Phone Number]. \n\nLooking forward to assisting you! \n\nBest regards, \nSuman Poluri \n(210) 569-2034";
  const close = () => props.setshowGenerateEmail({ open: false, type: "" });
  return (
    <div className="flex-col-centered w-[35vw]">
      <div className="flex-row-centered justify-between items-center px-4 py-3">
        <div className="flex-row-centered gap-4">
          <EmailIcon />
          <span className="sm-600 text-grey-700">Generate email</span>
        </div>
        <CloseOutlined className="text-[16px] cursor-pointer" onClick={close} />
      </div>
      <div className="h-[1px] bg-grey-200 w-full mt-1" />
      <div className="px-4 py-3">
        <span dangerouslySetInnerHTML={{ __html: text }} className="whitespace-pre-line" />
      </div>
      <div className="flex-row-centered items-center gap-2 p-2">
        <Button
          onClick={() => {
            props.emailTextRef.current.innerText = `${text}\n${props.emailTextRef.current.innerText}`;
            close();
          }}
          type="primary"
        >
          Apply
        </Button>
        <Button onClick={close}>Cancel</Button>
      </div>
    </div>
  );
};
export const GenerateSubjectJSX = props => {
  const text = "Test email subject";
  const close = () => props.setshowGenerateEmail({ open: false, type: "" });
  return (
    <div className="flex-col-centered w-[35vw]">
      <div className="flex-row-centered justify-between items-center px-4 py-3">
        <div className="flex-row-centered gap-4">
          <EmailIcon />
          <span className="sm-600 text-grey-700">Generate subject</span>
        </div>
        <CloseOutlined className="text-[16px] cursor-pointer" onClick={close} />
      </div>
      <div className="h-[1px] bg-grey-200 w-full mt-1" />
      <div className="px-4 py-3">
        <span dangerouslySetInnerHTML={{ __html: text }} className="whitespace-pre-line" />
      </div>
      <div className="flex-row-centered items-center gap-2 p-2">
        <Button
          onClick={() => {
            // props.emailTextRef.current.innerText = text;
            props.emailForm.setFieldValue("subject", text);
            close();
          }}
          type="primary"
        >
          Apply
        </Button>
        <Button onClick={close}>Cancel</Button>
      </div>
    </div>
  );
};
